import axios from 'axios'
import React, { useEffect, useMemo, useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'
import { Box, Modal } from '@mui/material'

const palette = ['#16a34a', '#0ea5e9', '#f59e0b']

const cardStyle = {
  background: '#ffffff',
  border: '1px solid #e5e7eb',
  borderRadius: '16px',
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)'
}

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  maxWidth: '90%',
  width: '568px',
  borderRadius: '24px',
  bgcolor: 'white',
  boxShadow: '2px 2px 8px rgba(83, 83, 83, 0.12)',
  padding: '32px'
}

const formatMonthLabel = value => {
  if (!value) return '--'

  const date = new Date(`${value}-01T00:00:00`)

  return date.toLocaleDateString('en-US', {
    month: 'short',
    year: '2-digit'
  })
}

const formatDate = value => {
  if (!value) return '--'

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) return '--'

  return date.toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  })
}

const formatQueryDate = value => {
  if (!value) return ''
  return value
}

const formatTimelineLabel = timeline => {
  if (!timeline) return 'Overall'

  if (typeof timeline === 'string') return timeline

  if (typeof timeline === 'object') {
    const start = timeline?.start || ''
    const end = timeline?.end || ''

    if (start && end) return `${start} - ${end}`
    if (start) return start
    if (end) return end
  }

  return 'Overall'
}

const formatFeatureName = value => {
  if (!value) return '--'

  const v =  value
    .split('.')
    .join(' | ')
    .split('_')
    .join(' ')
    .replace(/\b\w/g, char => char.toUpperCase())

    return v
}

const formatNumber = value => {
  const numericValue = Number(value || 0)

  return numericValue.toLocaleString('en-US')
}

const StatCard = ({ title, value, subtitle, timeline, onClick }) => (
  <div
    style={{
      ...cardStyle,
      padding: '20px 22px',
      minHeight: '132px',
      cursor: onClick ? 'pointer' : 'default'
    }}
    onClick={onClick}
  >
    <p
      style={{
        margin: '8px 0 8px auto',
        fontSize: '12px',
        background: '#ccc',
        padding: '4px',
        borderRadius: '4px',
        width: 'fit-content'
      }}
    >
      {formatTimelineLabel(timeline)}
    </p>
    <p
      style={{
        margin: 0,
        color: '#64748b',
        fontSize: '13px',
        fontWeight: 600,
        letterSpacing: '0.02em'
      }}
    >
      {title}
    </p>
    <h3
      style={{
        margin: '12px 0 8px',
        color: '#0f172a',
        fontSize: '32px',
        fontWeight: 700
      }}
    >
      {value}
    </h3>
    <p style={{ margin: 0, color: '#475569', fontSize: '12px' }}>{subtitle}</p>
  </div>
)

const Usage = () => {
  const [usageData, setUsageData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedCard, setSelectedCard] = useState(null)

  const fetchStats = async (filters = {}) => {
    try {
      setLoading(true)
      setError('')

      const rangeParams = {}

      if (filters.startDate) {
        rangeParams.startDate = formatQueryDate(filters.startDate)
      }

      if (filters.endDate) {
        rangeParams.endDate = formatQueryDate(filters.endDate)
      }

      const [
        usageRes,
        totalPaidUsersRes,
        activePaidUsersRes,
        churnedUsersRes,
        featureWiseMostHitUsersRes,
        thisMonthRes
      ] = await Promise.all([
        axios.get('/api/activity/get-usage', { params: rangeParams }),
        axios.get('/api/activity/total-paid-user', { params: rangeParams }),
        axios.get('/api/activity/active-paid-users'),
        axios.get('/api/activity/churned-users', { params: rangeParams }),
        axios.get('/api/activity/feature-wise-most-hit-users'),
        axios.get('/api/activity/this-month')
      ])

      setUsageData({
        memberMonthly: usageRes?.data?.data?.memberMonthly || null,
        limitsMostHit: usageRes?.data?.data?.limitsMostHit || null,
        totalPaidUsers: totalPaidUsersRes?.data?.data || null,
        activePaidUsers: activePaidUsersRes?.data?.data || null,
        churnedUsers: churnedUsersRes?.data?.data || null,
        featureWiseMostHitUsers: featureWiseMostHitUsersRes?.data?.data || null,
        thisMonth: thisMonthRes?.data?.data || null
      })
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load usage analytics')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchStats()
  }, [])

  const handleApplyFilters = () => {
    fetchStats({ startDate, endDate })
  }

  const handleResetFilters = () => {
    setStartDate('')
    setEndDate('')
    fetchStats()
  }

  const handleCardClick = cardData => {
    setSelectedCard(cardData)
    setModalOpen(true)
  }

  const handleModalClose = () => {
    setModalOpen(false)
    setSelectedCard(null)
  }

  const months = usageData?.memberMonthly?.data?.months || []
  const range = usageData?.memberMonthly?.data?.range || {}
  const limitsMostHit = usageData?.limitsMostHit || {}
  const rankedLimits = limitsMostHit?.all_ranked || []
  // const highlightedLimit = limitsMostHit?.most_hit || null;
  const totalPaidUsers = usageData?.totalPaidUsers?.data?.totalPaidUsers || 0
  const totalPaidUsersRange = usageData?.totalPaidUsers?.data?.range || 0
  const paidUserTimeline = usageData?.totalPaidUsers?.data?.timeline || []
  const activePaidUsers = usageData?.activePaidUsers?.data || {}
  const churnedUsers = usageData?.churnedUsers?.data?.churnedUsers || 0
  const churnedUsersRange = usageData?.churnedUsers?.data?.range || 0
  const churnedTimeline = usageData?.churnedUsers?.data?.timeline || []
  const featureUsageGroups = usageData?.featureWiseMostHitUsers?.data || []

  const thisMonthSummary = usageData?.thisMonth || {}

  const chartData = useMemo(
    () =>
      months.map(item => ({
        ...item,
        label: formatMonthLabel(item.month)
      })),
    [months]
  )

  // const summary = useMemo(() => {
  //   return months.reduce(
  //     (acc, item) => {
  //       acc.totalNewMembers += item.totalNewMembers || 0;
  //       acc.newPremiumMembers += item.newPremiumMembers || 0;
  //       return acc;
  //     },
  //     { totalNewMembers: 0, newPremiumMembers: 0 }
  //   );
  // }, [months]);

  // const topMonth = useMemo(() => {
  //   if (!months.length) return null;

  //   return months.reduce((max, current) =>
  //     current.totalNewMembers > max.totalNewMembers ? current : max
  //   );
  // }, [months]);

  // const totalLimitHits = useMemo(
  //   () =>
  //     rankedLimits.reduce((acc, item) => {
  //       return acc + (item.weeklyHits || 0);
  //     }, 0),
  //   [rankedLimits]
  // );

  if (loading) {
    return (
      <div className='font18' style={{ padding: '24px', color: '#475569' }}>
        Loading usage analytics...
      </div>
    )
  }

  if (error) {
    return (
      <div className='font18' style={{ padding: '24px' }}>
        <div
          style={{
            ...cardStyle,
            padding: '18px 20px',
            color: '#b91c1c',
            background: '#fef2f2',
            borderColor: '#fecaca'
          }}
        >
          {error}
        </div>
      </div>
    )
  }

  return (
    <div className='font18'>
      <div
        style={{
          ...cardStyle,
          padding: '24px',
          marginBottom: '20px'
        }}
      >
        <div className='d-flex justify-content-between align-items-start flex-wrap gap-3'>
          <div>
            <h2
              style={{
                margin: 0,
                color: '#0f172a',
                fontSize: '28px',
                fontWeight: 700
              }}
            >
              Usage Analytics
            </h2>
            <p
              style={{
                margin: '6px 0 0',
                color: '#0f172a',
                fontSize: '18px',
                fontWeight: 600,
                marginTop: '4px'
              }}
            >
              From {formatDate(range.start)} to {formatDate(range.end)}
            </p>
          </div>
          <div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '10px'
              }}
            >
              <div>
                <p
                  style={{
                    margin: '0 0 6px',
                    color: '#64748b',
                    fontSize: '12px'
                  }}
                >
                  From
                </p>
                <input
                  type='date'
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  style={{
                    width: '100%',
                    height: '38px',
                    padding: '0 12px',
                    borderRadius: '10px',
                    border: '1px solid #cbd5e1',
                    background: '#ffffff',
                    color: '#0f172a',
                    outline: 'none'
                  }}
                />
              </div>
              <div>
                <p
                  style={{
                    margin: '0 0 6px',
                    color: '#64748b',
                    fontSize: '12px'
                  }}
                >
                  To
                </p>
                <input
                  type='date'
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  style={{
                    width: '100%',
                    height: '38px',
                    padding: '0 12px',
                    borderRadius: '10px',
                    border: '1px solid #cbd5e1',
                    background: '#ffffff',
                    color: '#0f172a',
                    outline: 'none'
                  }}
                />
              </div>
            </div>
            <div className='d-flex flex-wrap gap-2 mt-3'>
              <button
                type='button'
                onClick={handleApplyFilters}
                style={{
                  height: '38px',
                  padding: '0 16px',
                  borderRadius: '10px',
                  border: 'none',
                  background: '#0f172a',
                  color: '#ffffff',
                  fontSize: '13px',
                  fontWeight: 600
                }}
              >
                Apply
              </button>
              <button
                type='button'
                onClick={handleResetFilters}
                style={{
                  height: '38px',
                  padding: '0 16px',
                  borderRadius: '10px',
                  border: '1px solid #cbd5e1',
                  background: '#ffffff',
                  color: '#334155',
                  fontSize: '13px',
                  fontWeight: 600
                }}
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '16px',
          marginBottom: '20px'
        }}
      >
        {/* <StatCard
          title="Total New Members"
          value={formatNumber(summary.totalNewMembers)}
          subtitle={`${months.length} tracked months`}
          accent="#16a34a"
        />
        <StatCard
          title="New Premium Members"
          value={formatNumber(summary.newPremiumMembers)}
          subtitle="Premium conversions in the same period"
          accent="#0ea5e9"
        />
        <StatCard
          title="Highest Member Growth Month"
          value={formatNumber(topMonth ? topMonth.totalNewMembers : 0)}
          subtitle={topMonth ? formatMonthLabel(topMonth.month) : "No monthly data"}
          accent="#f59e0b"
        />
        <StatCard
          title="Limit Hits Last 7 Days (Most)"
          value={formatNumber(totalLimitHits)}
          subtitle={
            highlightedLimit
              ? `${formatFeatureName(highlightedLimit.limit)}`
              : "No limit hits recorded"
          }
          accent="#8b5cf6"
        /> */}
        <StatCard
          title='Total Paid Users'
          value={formatNumber(totalPaidUsers)}
          timeline={totalPaidUsersRange}
          onClick={() =>
            handleCardClick({
              title: 'Total Paid Users',
              value: formatNumber(totalPaidUsers),
              subtitle: 'Total users who have made payments',
              timeline: totalPaidUsersRange,
              timelineData: paidUserTimeline
            })
          }
        />
        <StatCard
          title='Active Premium Users'
          value={formatNumber(activePaidUsers?.currentActivePremium || 0)}
          subtitle='Current active premium accounts'
          accent='#dc2626'
          onClick={() =>
            handleCardClick({
              title: 'Active Premium Users',
              value: formatNumber(activePaidUsers?.currentActivePremium || 0),
              subtitle: 'Current active premium accounts',
              accent: '#dc2626',
              timelineData:activePaidUsers?.premiumUsers
            })
          }
        />
        <StatCard
          title='360 Readiness Users'
          value={formatNumber(activePaidUsers?.current360ReadinessUsers || 0)}
          subtitle='Users with 360 readiness service'
          accent='#7c3aed'
          onClick={() =>
            handleCardClick({
              title: '360 Readiness Users',
              value: formatNumber(
                activePaidUsers?.current360ReadinessUsers || 0
              ),
              subtitle: 'Users with 360 readiness service',
              accent: '#7c3aed',
               timelineData:activePaidUsers?.readinessUsers
            })
          }
        />
        <StatCard
          title='Churned Users'
          value={formatNumber(churnedUsers)}
          subtitle='Former subscribers'
          accent='#ea580c'
          timeline={churnedUsersRange}
          onClick={() =>
            handleCardClick({
              title: 'Churned Users',
              value: formatNumber(churnedUsers),
              subtitle: 'Former subscribers',
              accent: '#ea580c',
              timeline: churnedUsersRange,
              timelineData: churnedTimeline
            })
          }
        />
        <StatCard
          title='New Users This Month'
          value={formatNumber(thisMonthSummary?.currentMonthUserCount || 0)}
          accent='#15803d'
          timeline={'This Month'}
          onClick={() =>
            handleCardClick({
              title: 'New Users This Month',
              value: formatNumber(thisMonthSummary?.currentMonthUserCount || 0),
              accent: '#15803d',
              timeline: 'This Month',
              timelineData: thisMonthSummary?.currentMonthUsers
            })
          }
        />
        <StatCard
          title='New Subscriptions This Month'
          value={formatNumber(
            thisMonthSummary?.totalSubscriptions || 0
          )}
          accent='#1d4ed8'
          timeline={'This Month'}
          onClick={() =>
            handleCardClick({
              title: 'New Subscriptions This Month',
              value: formatNumber(
                thisMonthSummary?.totalSubscriptions || 0
              ),
              accent: '#1d4ed8',
              timeline: 'This Month'
            })
          }
        />
      </div>

      <div
        style={{
          display: 'grid',
          gap: '20px'
        }}
      >
        <div style={{ ...cardStyle, padding: '20px' }}>
          <div>
            <h4
              style={{
                margin: 0,
                color: '#0f172a',
                fontSize: '18px',
                fontWeight: 700
              }}
            >
              Monthly Member Growth
            </h4>
            <p
              style={{ margin: '4px 0 0', color: '#64748b', fontSize: '13px' }}
            >
              New members vs premium members by month
            </p>
          </div>

          <div style={{ width: '100%', height: '360px' }}>
            <ResponsiveContainer>
              <BarChart
                data={chartData}
                margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray='3 3' stroke='#e2e8f0' />
                <XAxis
                  dataKey='label'
                  tick={{ fill: '#64748b', fontSize: 12 }}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fill: '#64748b', fontSize: 12 }}
                />
                <Tooltip
                  cursor={{ fill: 'rgba(148, 163, 184, 0.08)' }}
                  contentStyle={{
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)'
                  }}
                />
                <Bar
                  dataKey='totalNewMembers'
                  name='Total New Members'
                  fill='#16a34a'
                  radius={[8, 8, 0, 0]}
                />
                <Bar
                  dataKey='newPremiumMembers'
                  name='New Premium Members'
                  fill='#0ea5e9'
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {rankedLimits.length > 0 && (
        <div style={{ ...cardStyle, padding: '20px', marginTop: '20px' }}>
          <h4
            style={{
              margin: '0 0 16px',
              color: '#0f172a',
              fontSize: '18px',
              fontWeight: 700
            }}
          >
            Weekly Limit Hit Distribution
          </h4>
          <div style={{ width: '100%', height: '280px' }}>
            <ResponsiveContainer>
              <BarChart
                data={rankedLimits.map(item => ({
                  ...item,
                  shortLabel: formatFeatureName(item.feature)
                }))}
                layout='vertical'
                margin={{ top: 10, right: 10, left: 20, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray='3 3' stroke='#e2e8f0' />
                <XAxis
                  type='number'
                  allowDecimals={false}
                  tick={{ fill: '#64748b' }}
                />
                <YAxis
                  type='category'
                  dataKey='shortLabel'
                  tick={{ fill: '#475569', fontSize: 12 }}
                  width={120}
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)'
                  }}
                />
                <Bar
                  dataKey='weeklyHits'
                  name='Weekly Hits'
                  radius={[0, 8, 8, 0]}
                >
                  {rankedLimits.map((item, index) => (
                    <Cell
                      key={`${item.limit}-${index}`}
                      fill={palette[index % palette.length]}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}


        <div style={{ ...cardStyle, padding: '20px', marginTop: '20px' }}>
          <div className='d-flex justify-content-between align-items-center mb-3'>
            <h4
              style={{
                margin: 0,
                color: '#0f172a',
                fontSize: '18px',
                fontWeight: 700
              }}
            >
              Feature-wise Most Hit Users
            </h4>
          </div>
          {featureUsageGroups.length ? (
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '14px'
                }}
              >
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        borderBottom: '1px solid #e2e8f0',
                        fontWeight: 600,
                        color: '#0f172a',
                        fontSize: '13px'
                      }}
                    >
                      Feature
                    </th>
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        borderBottom: '1px solid #e2e8f0',
                        fontWeight: 600,
                        color: '#0f172a',
                        fontSize: '13px'
                      }}
                    >
                      User
                    </th>
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'right',
                        borderBottom: '1px solid #e2e8f0',
                        fontWeight: 600,
                        color: '#0f172a',
                        fontSize: '13px'
                      }}
                    >
                      Total Hits
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {featureUsageGroups.map((item, index) => (
                    <tr
                      key={`${item.limit}-${item.user?._id || index}`}
                      style={{
                        borderBottom: '1px solid #f1f5f9',
                        background: index % 2 === 0 ? '#ffffff' : '#f8fafc'
                      }}
                    >
                      <td
                        style={{
                          padding: '12px 16px',
                          color: '#0f172a',
                          fontWeight: 500
                        }}
                      >
                        {formatFeatureName(item.feature)}
                      </td>
                      <td
                        style={{
                          padding: '12px 16px',
                          color: '#0f172a'
                        }}
                      >
                        <div>
                          <p
                            style={{
                              margin: 0,
                              fontWeight: 600,
                              fontSize: '14px'
                            }}
                          >
                            {item.topUser?.name ||
                              `${item?.topUser?.firstname || ''} ${
                                item?.topUser?.lastname || ''
                              }`.trim() ||
                              '--'}
                          </p>
                          <p
                            style={{
                              margin: '2px 0 0',
                              color: '#64748b',
                              fontSize: '12px'
                            }}
                          >
                            {item?.topUser?.email || '--'}
                          </p>
                        </div>
                      </td>
                      <td
                        style={{
                          padding: '12px 16px',
                          textAlign: 'right',
                          color: '#0f172a',
                          fontWeight: 600,
                          fontSize: '16px'
                        }}
                      >
                        {formatNumber(item?.topUser?.totalHits || 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>
              No feature usage rankings available.
            </p>
          )}
        </div>
    

      <Modal
        open={modalOpen}
        onClose={handleModalClose}
        aria-labelledby='stat-card-modal-title'
        aria-describedby='stat-card-modal-description'
      >
        <Box sx={modalStyle}>
          {selectedCard && (
            <div>
              <h2
                id='stat-card-modal-title'
                style={{
                  margin: '0 0 16px',
                  color: '#0f172a',
                  fontSize: '24px',
                  fontWeight: 700
                }}
              >
                {selectedCard.title}
              </h2>
              {selectedCard.timelineData &&
                selectedCard.timelineData.length > 0 && (
                  <div>
                    <div
                      style={{
                        maxHeight: '300px',
                        overflowY: 'auto',
                        display: 'grid',
                        gap: '8px'
                      }}
                    >
                      {selectedCard.timelineData.map((item, index) => (
                        <div
                          key={index}
                          style={{
                            padding: '8px 12px',
                            background: '#f8fafc',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0'
                          }}
                        >
                          <p
                            style={{
                              margin: 0,
                              color: '#0f172a',
                              fontSize: '18px',
                              fontWeight: 'bold'
                            }}
                          >
                            {item.name || `${item.firstname || ''} ${item.lastname || ''}`.trim()}
                          </p>
                          <p
                            className='mt-2'
                            style={{
                              margin: 0,
                              color: '#0f172a',
                              fontSize: '14px'
                            }}
                          >
                            {' '}
                            {item.email}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          )}
        </Box>
      </Modal>
    </div>
  )
}

export default Usage
